'use strict';

import React, { useState, useContext, useEffect, useCallback } from 'react';
import { LangContext } from '../App';
import Header from '../components/Header';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Captures de la communauté',
    sousTitre: 'Les captures publiques partagées par les membres ChasseHunt.',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger les captures.',
    aucune: 'Aucune capture publique pour le moment.',
    par: 'Par',
    activites: {
      chasse: 'Chasse',
      piegeage: 'Piégeage',
      autre: 'Autre',
    },
    precedent: '← Précédent',
    suivant: 'Suivant →',
    page: 'Page',
  },
  en: {
    title: 'Community harvests',
    sousTitre: 'Public harvests shared by ChasseHunt members.',
    chargement: 'Loading…',
    erreur: 'Could not load harvests.',
    aucune: 'No public harvests yet.',
    par: 'By',
    activites: {
      chasse: 'Hunting',
      piegeage: 'Trapping',
      autre: 'Other',
    },
    precedent: '← Previous',
    suivant: 'Next →',
    page: 'Page',
  },
};

const OR = 'var(--couleur-or)';
const TEXTE = 'var(--couleur-texte)';
const TEXTE_DIM = 'rgba(255,255,255,0.55)';
const BORDURE = 'rgba(255,255,255,0.1)';
const LIMITE = 24;

export default function CommunityHarvests() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);

  const charger = useCallback(async (p) => {
    setCharge(true);
    setErreur(false);
    try {
      const res = await api.get('/public/hunts', { params: { page: p, limite: LIMITE } });
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {
      setErreur(true);
      setItems([]);
    } finally {
      setCharge(false);
    }
  }, []);

  useEffect(() => {
    charger(page);
  }, [page, charger]);

  const totalPages = Math.ceil(total / LIMITE);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--couleur-fond)', color: TEXTE }}>
      <Header />
      <main style={{ maxWidth: 1080, margin: '0 auto', padding: '2rem 1rem 4rem' }}>
        <PageHeader icon="🦌" title={t.title} subtitle={t.sousTitre} />

        {chargement && (
          <div
            style={{
              color: TEXTE_DIM,
              fontSize: '0.9rem',
              padding: '1.5rem',
              textAlign: 'center',
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${BORDURE}`,
              borderRadius: 8,
            }}
          >
            {t.chargement}
          </div>
        )}

        {!chargement && erreur && (
          <div
            style={{
              background: 'rgba(200,50,50,0.1)',
              border: '1px solid rgba(200,50,50,0.35)',
              borderRadius: 8,
              padding: '0.9rem 1rem',
              color: 'var(--couleur-texte-erreur)',
              fontSize: '0.9rem',
            }}
          >
            ⚠ {t.erreur}
          </div>
        )}

        {!chargement && !erreur && items.length === 0 && (
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(184,134,11,0.4)',
              borderRadius: 8,
              padding: '2rem 1rem',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.45)',
              fontSize: '0.95rem',
            }}
          >
            {t.aucune}
          </div>
        )}

        {!chargement && !erreur && items.length > 0 && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '0.9rem',
              }}
            >
              {items.map((h) => {
                const date = h.photo_taken_at
                  ? new Date(h.photo_taken_at).toLocaleDateString(
                      lang === 'fr' ? 'fr-CA' : 'en-CA',
                      {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }
                    )
                  : '';
                const activite = t.activites[h.activity_type];
                return (
                  <article
                    key={h.id}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${BORDURE}`,
                      borderRadius: 10,
                      overflow: 'hidden',
                      display: 'grid',
                      gridTemplateRows: 'auto 1fr',
                    }}
                  >
                    <div
                      style={{
                        aspectRatio: '4 / 3',
                        background: `url(${h.photo_url}) center/cover no-repeat`,
                        borderBottom: `1px solid ${BORDURE}`,
                      }}
                    />
                    <div
                      style={{ padding: '0.7rem 0.9rem 0.9rem', display: 'grid', gap: '0.35rem' }}
                    >
                      {h.species && (
                        <div
                          style={{
                            fontFamily: 'Georgia, serif',
                            fontSize: '0.98rem',
                            color: TEXTE,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        >
                          {h.species}
                        </div>
                      )}
                      <div style={{ fontSize: '0.78rem', color: TEXTE_DIM }}>
                        {t.par} <span style={{ color: TEXTE }}>{h.author_name || '—'}</span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.4rem',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          marginTop: '0.2rem',
                        }}
                      >
                        {activite && (
                          <span
                            style={{
                              fontSize: '0.66rem',
                              color: OR,
                              background: 'rgba(201,162,39,0.12)',
                              border: '1px solid rgba(201,162,39,0.4)',
                              borderRadius: 20,
                              padding: '2px 8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            {activite}
                          </span>
                        )}
                        {date && (
                          <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                            {date}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: '0.7rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '1.5rem',
                }}
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${BORDURE}`,
                    color: page <= 1 ? 'rgba(255,255,255,0.3)' : TEXTE,
                    borderRadius: 6,
                    padding: '0.4rem 0.9rem',
                    fontSize: '0.85rem',
                    cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t.precedent}
                </button>
                <span style={{ fontSize: '0.85rem', color: TEXTE_DIM }}>
                  {t.page} {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${BORDURE}`,
                    color: page >= totalPages ? 'rgba(255,255,255,0.3)' : TEXTE,
                    borderRadius: 6,
                    padding: '0.4rem 0.9rem',
                    fontSize: '0.85rem',
                    cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t.suivant}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
